import { intersectionWith, isEqual, mergeWith, unionWith } from 'lodash-es'
// import Compressor from 'compressorjs'
import { ElMessage } from 'element-plus'
import { name } from '../../package.json'
import { getOrientation, isPcDevice } from './device'
import { isArray, isObject } from '@/utils/is'

export const camelize = (str: string): string => {
  const camelizeRE = /-(\w)/g
  return str.replace(camelizeRE, (_, c) => c.toUpperCase())
}

/** 将驼峰格式转换为连接符分隔的形式 */
export function camelToHyphen(camelCaseStr) {
  return camelCaseStr.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

export function getAppCode() {
  return `${camelize(import.meta.env.VITE_APP_CODE || 'STORE_')}`
}

export function getPkgName() {
  return `${camelize(name)}`
}

// px2vw
export const px2vw = (px: number | string): string => {
  // 在pc端或横屏下不转换
  if (isPcDevice() || !getOrientation()) {
    return String(px)
  }
  const designWidth = Number(process.env.VITE_VW_WIDTH)
  return `${(100 / designWidth) * Number(px)}vw`
}

/** 获取 url 后缀名 */
export interface IUrlSuffix {
  /** 后缀完整名字 例如：name.jpg */
  fullName: string
  /** 后缀文件名 例如：name */
  name: string
  /** 文件类型 例如：.jpg */
  suffix: string
  /** 文件类型名 例如：jpg */
  suffixName: string
  /** 排除后缀后的URL 例如：http://localhost:8000/img */
  url: string
}
export function getUrlSuffix(URL): IUrlSuffix {
  const last = URL.lastIndexOf('/')
  /** 截取文件名称和后缀 */
  const fullName = URL.substring(last + 1)
  /** 截取路径字符串 */
  const url = URL.substring(0, last)
  /** 截取文件名 */
  const name = fullName.substring(0, fullName.lastIndexOf('.'))
  /** 截取后缀 */
  const suffix = URL.substring(URL.lastIndexOf('.'))
  /** 截取后缀名 */
  const suffixName = URL.substring(URL.lastIndexOf('.') + 1)

  return {
    fullName,
    name,
    suffix,
    suffixName,
    url,
  }
}

/**
 * Recursively merge two objects.
 * 递归合并两个对象。
 *
 * @param source The source object to merge from. 要合并的源对象。
 * @param target The target object to merge into. 目标对象，合并后结果存放于此。
 * @param mergeArrays How to merge arrays. Default is "replace".
 *        如何合并数组。默认为replace。
 *        - "union": Union the arrays. 对数组执行并集操作。
 *        - "intersection": Intersect the arrays. 对数组执行交集操作。
 *        - "concat": Concatenate the arrays. 连接数组。
 *        - "replace": Replace the source array with the target array. 用目标数组替换源数组。
 * @returns The merged object. 合并后的对象。
 */
export function deepMerge<T extends object | null | undefined, U extends object | null | undefined>(
  source: T,
  target: U,
  mergeArrays: 'union' | 'intersection' | 'concat' | 'replace' = 'replace',
): T & U {
  if (!target)
    return source as T & U

  if (!source)
    return target as T & U

  return mergeWith({}, source, target, (sourceValue, targetValue) => {
    if (isArray(targetValue) && isArray(sourceValue)) {
      switch (mergeArrays) {
        case 'union':
          return unionWith(sourceValue, targetValue, isEqual)
        case 'intersection':
          return intersectionWith(sourceValue, targetValue, isEqual)
        case 'concat':
          return sourceValue.concat(targetValue)
        case 'replace':
          return targetValue
        default:
          throw new Error(`Unknown merge array strategy: ${mergeArrays as string}`)
      }
    }
    if (isObject(targetValue) && isObject(sourceValue))
      return deepMerge(sourceValue, targetValue, mergeArrays)

    return undefined
  })
}

/**
 * Add the object as a parameter to the URL
 * @param baseUrl url
 * @param obj
 * @returns {string}
 * eg:
 *  let obj = {a: '3', b: '4'}
 *  setObjToUrlParams('www.baidu.com', obj)
 *  ==>www.baidu.com?a=3&b=4
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  let parameters = ''
  for (const key in obj)
    parameters += `${key}=${encodeURIComponent(obj[key])}&`

  parameters = parameters.replace(/&$/, '')
  return /\?$/.test(baseUrl) ? baseUrl + parameters : baseUrl.replace(/\/?$/, '?') + parameters
}

/** 压缩图片，
 * jpg才受quality影响，
 * png可通过设置 maxWidth 来限制
 */
export async function compressor(file) {
  return new Promise((resolve, reject) => {
    // new Compressor(file, {
    //   // 默认 jpg：0.92，webp：0.8
    //   // quality: 0.8,
    //   // 图片最大宽度
    //   maxWidth: 1000,
    //   // png 超过5兆默认会压缩成jpg，设置为 Infinity 不生效
    //   // convertSize: 5000000,
    //   success: resolve,
    //   error: reject,
    // })
  })
}

// 随机数
export const getRandom = (num = 16) => {
  const len = num
  const $chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const maxPos = $chars.length
  let pwd = ''
  for (let i = 0; i < len; i++) {
    pwd += $chars.charAt(Math.floor(Math.random() * maxPos))
  }

  return pwd
}

// 随机数
export const getRandomNumber = (num = 8): number => {
  const len = num
  const $chars = '0123456789'
  const maxPos = $chars.length
  let pwd = ''
  for (let i = 0; i < len; i++) {
    pwd += $chars.charAt(Math.floor(Math.random() * maxPos))
  }

  return Number(pwd)
}

/**
 * 更换网站图标
 * @param src
 */
export function changeFavicon(src: string) {
  let link: HTMLLinkElement = document.querySelector('link[rel~=\'icon\']')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.getElementsByTagName('head')[0].appendChild(link)
  }
  link.href = src
}

/**
 * 根据规则转换数据
 * @param data
 * @param rules
 * @returns
 */
export function formatApiData(data: any, rules: any) {
  if (Array.isArray(data)) {
    return data.map(item => formatApiData(item, rules))
  } else if (typeof data === 'object' && data !== null) {
    const transformedData = {}

    for (const key in rules) {
      if (key in data) {
        const rule = rules[key]
        if (typeof rule === 'object' && !Array.isArray(rule)) {
          transformedData[rule.origin] = formatApiData(data[key], rule)
        } else {
          transformedData[rule.origin] = data[key]
        }
      }
    }

    return transformedData
  } else {
    return data
  }
}

/** 16进制颜色转rgb */
export function hexToRgb(hex: string, alpha?: number) {
  // Remove the leading #
  hex = hex.replace(/^#/, '')

  // If the hex value is in shorthand form, expand it to the full form
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('')
  }

  // Convert the hex value to RGB
  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `rgba(${r}, ${g}, ${b}, ${(alpha || alpha === 0) ? alpha : 1})`
}

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc && acc[key], obj)
}
/**
 * 将数组中的元素按指定的键或函数分组 并保留原始数组顺序
 * @param {Array} array - 要分组的数组
 * @param {Function|String} iteratee - 用于分组的函数或属性名
 * @returns {Map} 分组后的 Map 对象
 */
export function groupBy(array, iteratee) {
  return array.reduce((result, item) => {
    const key = typeof iteratee === 'function' ? iteratee(item) : getNestedValue(item, iteratee)
    if (!result.has(key)) {
      result.set(key, [])
    }
    result.get(key).push(item)

    return result
  }, new Map())
}

/** 回到顶部 */
export function scrollTop(el, type?: string, number?: number) {
  el && el.scrollTo({
    top: number || 0,
    behavior: type || 'smooth',
  })
}

export const stripNum = (num: number, precision = 12): number => {
  return Number(parseFloat(num.toPrecision(precision)))
}

/** 格式化数字向下取整 默认2位小数 */
export function formatFloorNumber(n: number | string, decimalPlaces = 2): string {
  if (!n && n !== 0) return ''
  const num = Number(n)
  try {
    const factor = 10 ** (decimalPlaces)
    const flooredNum = Math.floor(stripNum(num * factor)) / factor
    return flooredNum.toFixed(decimalPlaces)
  } catch (err) {
    console.log('[formatFloorNumber error]', err)
  }
}

export function formatNumberWithCommas(number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

interface Item {
  [key: string]: any
}

export function updateArrayByAnother<T extends Item>(arrayA: T[], arrayB: T[], key: keyof T = 'id') {
  const bMap = new Map(arrayB.map(item => [item[key], item]))
  arrayA.forEach((item) => {
    if (bMap.has(item[key])) {
      Object.assign(item, bMap.get(item[key]))
    }
  })
}
